import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button, Modal, Table, Badge, Form } from "react-bootstrap";
import { useAppContext } from "../../contexts/AppContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { isImage, isVideo } from "../MediaFilter";
import thumbnailFallback from "../../assets/thumbnail.jpg";


const TestimonialsAdmin = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("view"); // "view" | "edit"
  const [currentTestimonial, setCurrentTestimonial] = useState(null);
  
  // Edit form states
  const [editedName, setEditedName] = useState("");
  const [editedEmail, setEditedEmail] = useState("");
  const [editedComment, setEditedComment] = useState("");
  const [oldImages, setOldImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  
  const [saving, setSaving] = useState(false);
  const { backendUrl, getImageUrl } = useAppContext();
  const testimonialRoute = "/testimonialsList";

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${backendUrl}${testimonialRoute}`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  }, [backendUrl, testimonialRoute]);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(testimonials);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    setTestimonials(updatedItems);

    try {
      await axios.put(`${backendUrl}${testimonialRoute}/reorder`, {
        items: updatedItems.map((item) => ({ id: item.id, order: item.order })),
      });
    } catch (error) {
      console.error("Error saving reorder:", error);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${backendUrl}${testimonialRoute}/${id}`, { isApproved: true });
      fetchTestimonials();
    } catch (error) {
      console.error("Error approving testimonial:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this testimonial?")) {
      try {
        await axios.delete(`${backendUrl}${testimonialRoute}/${id}`);
        fetchTestimonials();
      } catch (error) {
        console.error("Error deleting testimonial:", error);
      }
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setNewImages((prev) => [...prev, ...files]);
    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...previews]);
  };

  const handleRemoveNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveOldImage = (imagePath) => {
    setOldImages((prev) => prev.filter((img) => img !== imagePath));
    setImagesToDelete((prev) => [...prev, imagePath]);
  };

  const onDragEndImages = (result) => {
    if (!result.destination) return;
    const items = Array.from(oldImages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setOldImages(items);
  };

  const handleSaveEdit = async () => {
    if (!currentTestimonial) return;
    setSaving(true);
    
    const formData = new FormData();
    formData.append("name", editedName);
    formData.append("email", editedEmail);
    formData.append("comment", editedComment);
    formData.append("imagesToKeep", JSON.stringify(oldImages));
    formData.append("imagesToDelete", JSON.stringify(imagesToDelete));
    
    newImages.forEach((img) => formData.append("images", img));

    try {
      await axios.put(`${backendUrl}${testimonialRoute}/${currentTestimonial.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMode("view");
      fetchTestimonials();
      closeModal();
    } catch (error) {
      console.error("Error updating testimonial:", error);
      alert("Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const openModal = (testimonial, initialMode = "view") => {
    setCurrentTestimonial(testimonial);
    setEditedName(testimonial.name || "");
    setEditedEmail(testimonial.email || "");
    setEditedComment(testimonial.comment || "");
    setOldImages(testimonial.images || []);
    setNewImages([]);
    setImagePreviews([]);
    setImagesToDelete([]);
    setMode(initialMode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setMode("view");
    setCurrentTestimonial(null);
  };

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="fw-bold m-0">Testimonials Management</h5>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={fetchTestimonials}
          style={{ borderColor: "var(--primary-color)", color: "var(--primary-color)" }}
        >
          <i className="bi bi-arrow-clockwise"></i>
        </Button>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <DragDropContext onDragEnd={onDragEnd}>
          <Table hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "40px" }}></th>
                <th>User</th>
                <th>Comment</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <Droppable droppableId="testimonials-list">
              {(provided) => (
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {testimonials.map((item, index) => (
                    <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                      {(provided, snapshot) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={snapshot.isDragging ? "bg-light shadow-sm" : ""}
                        >
                          <td {...provided.dragHandleProps} className="text-muted">
                            <i className="bi bi-grip-vertical fs-5"></i>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div
                                className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2 fw-bold overflow-hidden"
                                style={{ width: "40px", height: "40px", color: "var(--primary-color)" }}
                              >
                                {item.images?.[0] ? (
                                  isImage(getImageUrl(item.images[0])) ? (
                                    <img src={getImageUrl(item.images[0])} alt="p" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  ) : isVideo(getImageUrl(item.images[0])) ? (
                                    <img src={thumbnailFallback} alt="v" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  ) : (
                                    <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light">
                                      <i className="bi bi-file-earmark-play"></i>
                                    </div>
                                  )
                                ) : (
                                  item.name?.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div>
                                <div className="fw-bold">{item.name}</div>
                                <div className="text-muted small">{item.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-truncate" style={{ maxWidth: "200px" }}>
                            {item.comment}
                          </td>
                          <td>
                            {item.isApproved ? (
                              <Badge bg="success-subtle" className="text-success border border-success border-opacity-25 rounded-pill px-3 py-2">
                                <i className="bi bi-check-circle-fill me-1"></i> Active
                              </Badge>
                            ) : (
                              <Badge bg="warning-subtle" className="text-warning border border-warning border-opacity-25 rounded-pill px-3 py-2">
                                <i className="bi bi-hourglass-split me-1"></i> Pending
                              </Badge>
                            )}
                          </td>
                          <td className="text-end">
                            {/* Flex container to prevent overlapping on mobile */}
                            <div className="d-flex justify-content-end gap-2 flex-wrap">
                              {!item.isApproved && (
                                <button
                                  className="btn-action approve"
                                  onClick={() => handleApprove(item.id)}
                                  title="Approve"
                                >
                                  <i className="bi bi-check-lg"></i>
                                </button>
                              )}
                              <button
                                className="btn-action view"
                                onClick={() => openModal(item, "view")}
                                title="View"
                              >
                                <i className="bi bi-eye-fill"></i>
                              </button>
                              <button
                                className="btn-action edit"
                                onClick={() => openModal(item, "edit")}
                                title="Edit"
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </button>
                              <button
                                className="btn-action delete"
                                onClick={() => handleDelete(item.id)}
                                title="Delete"
                              >
                                <i className="bi bi-trash3-fill"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      </div>

      {/* Modal */}
      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            {mode === "edit" ? (
              <><i className="bi bi-pencil-square me-2 text-primary"></i>Edit Testimonial</>
            ) : (
              <><i className="bi bi-chat-quote me-2 text-warning"></i>Testimonial Details</>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {currentTestimonial && (
            <div className="p-1 row g-3">
              <div className={mode === "edit" ? "col-md-6" : "col-12"}>
                <label className="text-muted small text-uppercase fw-bold mb-1">Name</label>
                {mode === "edit" ? (
                  <Form.Control value={editedName} onChange={(e) => setEditedName(e.target.value)} />
                ) : (
                  <div className="fw-bold mb-3">{currentTestimonial.name}</div>
                )}
              </div>

              <div className={mode === "edit" ? "col-md-6" : "col-12"}>
                <label className="text-muted small text-uppercase fw-bold mb-1">Email</label>
                {mode === "edit" ? (
                  <Form.Control value={editedEmail} onChange={(e) => setEditedEmail(e.target.value)} />
                ) : (
                  <div className="fw-bold mb-3">{currentTestimonial.email}</div>
                )}
              </div>

              <div className="col-12">
                <label className="text-muted small text-uppercase fw-bold mb-1">Comment</label>
                {mode === "edit" ? (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={editedComment}
                    onChange={(e) => setEditedComment(e.target.value)}
                  />
                ) : (
                  <div className="p-3 bg-light rounded-3 fst-italic mb-3">"{currentTestimonial.comment}"</div>
                )}
              </div>

              <div className="col-12 mt-3">
                <label className="text-muted small text-uppercase fw-bold mb-2">
                  Media (Drag to reorder)
                </label>
                
                {mode === "edit" && (
                  <div className="mb-3">
                    <Form.Control type="file" multiple onChange={handleImageChange} />
                  </div>
                )}

                {/* Existing Images Reordering */}
                <DragDropContext onDragEnd={onDragEndImages}>
                  <Droppable droppableId="modal-images" direction="horizontal">
                    {(provided) => (
                      <div 
                        className="d-flex flex-wrap gap-3 p-3 bg-light rounded-3" 
                        {...provided.droppableProps} 
                        ref={provided.innerRef}
                        style={{ minHeight: "110px" }}
                      >
                        {Array.isArray(oldImages) && oldImages.map((img, idx) => (
                          <Draggable key={img} draggableId={img} index={idx} isDragDisabled={mode === "view"}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`position-relative bg-white rounded shadow-sm border p-1 ${snapshot.isDragging ? "z-3 shadow-lg" : "z-1"}`}
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.9 : 1,
                                  border: snapshot.isDragging ? "2px solid var(--primary-color)" : "1px solid #dee2e6",
                                }}
                              >
                                {/* Drag Handle */}
                                <div 
                                  {...provided.dragHandleProps} 
                                  className="position-absolute top-0 start-0 p-2 bg-white rounded-bottom-end border-end border-bottom shadow-sm d-flex align-items-center justify-content-center"
                                  style={{ cursor: "grab", zIndex: 30, display: mode === "view" ? "none" : "flex", minWidth: "24px", minHeight: "24px" }}
                                >
                                  <i className="bi bi-grip-vertical text-muted"></i>
                                </div>

                                {isImage(getImageUrl(img)) ? (
                                  <img src={getImageUrl(img)} alt="attached" className="rounded" style={{ width: "80px", height: "80px", objectFit: "cover" }} />
                                ) : isVideo(getImageUrl(img)) ? (
                                  <img src={thumbnailFallback} alt="attached" className="rounded" style={{ width: "80px", height: "80px", objectFit: "cover" }} />
                                ) : (
                                  <div className="rounded d-flex align-items-center justify-content-center bg-light" style={{ width: "80px", height: "80px" }}>
                                    <i className="bi bi-file-earmark-play fs-3"></i>
                                  </div>
                                )}
                                
                                {mode === "edit" && (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 py-0 px-1 m-1 shadow-sm"
                                    style={{ zIndex: 10 }}
                                    onClick={() => handleRemoveOldImage(img)}
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                {/* New Previews */}
                {mode === "edit" && imagePreviews.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {imagePreviews.map((preview, idx) => (
                      <div key={idx} className="position-relative">
                        <img src={preview} alt="preview" className="rounded shadow-sm" style={{ width: "80px", height: "80px", objectFit: "cover" }} />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0 py-0 px-1 m-1"
                          onClick={() => handleRemoveNewImage(idx)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0">
          {mode === "edit" ? (
            <>
              <Button variant="secondary" onClick={() => setMode("view")} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={saving}
                style={{ background: "var(--primary-color)", border: "none" }}
              >
                {saving ? <><span className="spinner-border spinner-border-sm me-2" /> Saving...</> : "Save Changes"}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={closeModal}>Close</Button>
          )}
        </Modal.Footer>
      </Modal>

      <style>{`
        .btn-action {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          background: #f8fafc;
          cursor: pointer;
        }
        .btn-action.edit { color: var(--primary-color); }
        .btn-action.edit:hover { background: var(--primary-color); color: white; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(255,165,0,0.25); }
        .btn-action.delete { color: #ef4444; }
        .btn-action.delete:hover { background: #ef4444; color: white; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(239,68,68,0.25); }
        .btn-action.view { color: #0ea5e9; }
        .btn-action.view:hover { background: #0ea5e9; color: white; transform: translateY(-2px); }
        .btn-action.approve { color: #22c55e; }
        .btn-action.approve:hover { background: #22c55e; color: white; transform: translateY(-2px); }
      `}</style>
    </div>
  );
};

export default TestimonialsAdmin;
